import { Suspense, useRef, useState } from "react";

import { PlusOutlined } from "@ant-design/icons";
import { useTranslation } from "@impero-com/common/utils/i18n";
import { FieldError, TSTPagination, TSTTable, Button } from "@impero-com/ui";
import { RowSelectionState, OnChangeFn } from "@tanstack/react-table";
import { Typography, Drawer, Spin } from "antd";
import { Control, useController } from "react-hook-form";

import { TestingControlFormSchema } from "../testingControlSchema";

import * as theme from "./BulkControlsTable.css";
import { SelectControlsTable } from "./SelectControlsTable";
import { useBulkControlsTable } from "./useBulkControlsTable";

const { Text } = Typography;
interface BulkControlsTableProps {
    control: Control<TestingControlFormSchema>;
}
export function BulkControlsTable({ control }: BulkControlsTableProps) {
    const { t } = useTranslation();
    const [drawerOpen, setDrawerOpen] = useState(false);

    const { field, fieldState } = useController({
        name: "selectedControlIds",
        control,
    });

    const { table } = useBulkControlsTable(control, field);

    const [controlIdsSelection, setControlIdsSelection] =
        useState<RowSelectionState>({});

    const onDrawerClose = () => {
        setDrawerOpen(false);
        setControlIdsSelection({});
    };

    const selectedIds = Object.entries(controlIdsSelection)
        .filter(([, selected]) => selected)
        .map(([id]) => id);
    const handleSelect = () => {
        field.onChange([...(field.value ?? []), ...selectedIds]);
        onDrawerClose();
    };

    const tableRef = useRef<HTMLTableElement>(null);
    return (
        <>
            <SelectControlsDrawer
                open={drawerOpen}
                onClose={onDrawerClose}
                onSelect={handleSelect}
                selectDisabled={selectedIds.length === 0}
                control={control}
                onRowSelectionChange={setControlIdsSelection}
                selectedControlIdsFieldValue={field.value}
                rowSelection={controlIdsSelection}
            />

            {field.value?.length > 0 ? (
                <div className={theme.tableContainer}>
                    <div>
                        <TSTTable
                            tableRef={tableRef}
                            table={table}
                            noResultsFallback={null}
                            noSourceRowsFallback={null}
                        />
                        <TSTPagination
                            tableElement={tableRef}
                            table={table}
                            pageLabel={t("Pagination_Page")}
                            getRowCountLabel={(shown, count) =>
                                t("Pagination_RowCount", { shown, count })
                            }
                            getSinglePageRowCountLabel={(count) =>
                                t("Pagination_RowCountSinglePage", { count })
                            }
                        />
                    </div>
                    <div>
                        <Button
                            variant="link"
                            icon={<PlusOutlined />}
                            onClick={() => setDrawerOpen(true)}
                        >
                            {t("TestingControlView_Bulk_AddControls")}
                        </Button>
                    </div>
                </div>
            ) : (
                <Empty onClick={() => setDrawerOpen(true)} />
            )}
            <FieldError error={fieldState.error?.message} />
        </>
    );
}

interface EmptyProps {
    onClick: () => void;
}
function Empty({ onClick }: EmptyProps) {
    const { t } = useTranslation();
    return (
        <div className={theme.emptyContainer}>
            <div className={theme.emptyContainerContent}>
                <Text className={theme.noControlsText}>
                    {t("TestingControlView_Bulk_NoControls")}
                </Text>
                <Button size="large" variant="primary" onClick={onClick}>
                    {t("TestingControlView_Bulk_AddControls")}
                </Button>
            </div>
        </div>
    );
}

interface SelectControlsDrawerProps {
    open: boolean;
    onClose: () => void;
    onSelect: () => void;
    selectDisabled: boolean;
    rowSelection: RowSelectionState;
    onRowSelectionChange: OnChangeFn<RowSelectionState>;
    selectedControlIdsFieldValue: string[] | undefined;
    control: Control<TestingControlFormSchema>;
}
function SelectControlsDrawer({
    open,
    onClose,
    onSelect,
    selectDisabled,
    rowSelection,
    onRowSelectionChange,
    selectedControlIdsFieldValue,
    control,
}: SelectControlsDrawerProps) {
    const { t } = useTranslation();

    const buttons = (
        <div className={theme.drawerButtons}>
            <Button
                disabled={selectDisabled}
                size="large"
                variant="primary"
                onClick={onSelect}
            >
                {t("TestingControlView_Bulk_Select")}
            </Button>

            <Button size="large" variant="default" onClick={onClose}>
                {t("TestingControlView_Bulk_Cancel")}
            </Button>
        </div>
    );

    return (
        <Drawer
            open={open}
            onClose={onClose}
            size="large"
            closeIcon={null}
            title={t("TestingControlView_Bulk_DrawerTitle")}
            bodyStyle={{ padding: 0 }}
            placement="bottom"
            extra={buttons}
            destroyOnClose
        >
            <Suspense fallback={<Spin />}>
                <SelectControlsTable
                    rowSelection={rowSelection}
                    onRowSelectionChange={onRowSelectionChange}
                    selectedControlIdsFieldValue={selectedControlIdsFieldValue}
                    control={control}
                />
            </Suspense>
        </Drawer>
    );
}
